import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router';
import {
  adminAccountsApi,
  type AdminAccountDirection,
  type AdminAccountDto,
  type AdminAccountSort,
  type AdminBookStatus,
} from '../api/admin-accounts.js';
import { useAuth } from '../auth/context.js';
import { isApiError, userErrorMessage } from '../api/errors.js';
import { useI18n } from '../i18n/context.js';

type AccountFilter = 'all' | 'enabled' | 'disabled';
type AccountRole = 'user' | 'admin';

export function AdminAccountsPage(): JSX.Element {
  const { user } = useAuth();
  const { t, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<AdminAccountSort>('createdAt');
  const [direction, setDirection] = useState<AdminAccountDirection>('desc');
  const [filter, setFilter] = useState<AccountFilter>('all');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [newAccount, setNewAccount] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as AccountRole,
    emailVerified: true,
  });

  const accounts = useQuery({
    queryKey: ['admin-accounts', sort, direction],
    queryFn: () => adminAccountsApi.list(sort, direction),
    enabled: user?.role === 'admin',
  });
  const registration = useQuery({
    queryKey: ['admin-registration-status'],
    queryFn: adminAccountsApi.registrationStatus,
    enabled: user?.role === 'admin',
  });
  const economyMode = useQuery({
    queryKey: ['admin-economy-mode'],
    queryFn: adminAccountsApi.economyModeStatus,
    enabled: user?.role === 'admin',
  });
  const statusMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      adminAccountsApi.setEnabled(id, enabled),
    onSuccess: (result) => {
      setMessage(
        result.enabled ? t.admin.accounts.enabledMessage : t.admin.accounts.disabledMessage,
      );
      void queryClient.invalidateQueries({ queryKey: ['admin-accounts'] });
    },
    onError: (error: Error) => setMessage(error.message || t.common.unexpectedError),
  });
  const registrationMutation = useMutation({
    mutationFn: adminAccountsApi.setRegistrationEnabled,
    onSuccess: (result) => {
      queryClient.setQueryData(['admin-registration-status'], result);
      queryClient.setQueryData(['registration-status'], result);
      setMessage(t.admin.accounts.registrationUpdated);
    },
    onError: (error: Error) => setMessage(error.message || t.common.unexpectedError),
  });
  const economyModeMutation = useMutation({
    mutationFn: adminAccountsApi.setEconomyModeEnabled,
    onSuccess: (result) => {
      queryClient.setQueryData(['admin-economy-mode'], result);
      setMessage(t.admin.accounts.economyModeUpdated);
    },
    onError: (error: Error) => setMessage(error.message || t.common.unexpectedError),
  });
  const createMutation = useMutation({
    mutationFn: adminAccountsApi.create,
    onSuccess: () => {
      setMessage(t.admin.accounts.createdMessage);
      setNewAccount({ name: '', email: '', password: '', role: 'user', emailVerified: true });
      void queryClient.invalidateQueries({ queryKey: ['admin-accounts'] });
    },
    onError: (error: unknown) => {
      setMessage(
        isApiError(error) && error.code === 'weak_password'
          ? t.auth.register.weakPassword
          : isApiError(error) && error.code === 'conflict'
            ? t.admin.accounts.accountExists
            : userErrorMessage(error, t),
      );
    },
  });

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return (accounts.data?.items ?? []).filter((account) => {
      if (filter === 'enabled' && !account.enabled) return false;
      if (filter === 'disabled' && account.enabled) return false;
      return !needle || `${account.name} ${account.email}`.toLocaleLowerCase().includes(needle);
    });
  }, [accounts.data?.items, filter, search]);

  if (user?.role !== 'admin') {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <p role="alert">{t.admin.forbidden}</p>
        <Link to="/app" className="kitup-quiet-button mt-4">
          {t.admin.toApp}
        </Link>
      </main>
    );
  }

  const all = accounts.data?.items ?? [];
  const enabledCount = all.filter((account) => account.enabled).length;
  const booksCount = all.reduce((sum, account) => sum + account.bookCounts.total, 0);

  const changeStatus = (account: AdminAccountDto): void => {
    const nextEnabled = !account.enabled;
    if (!nextEnabled && !window.confirm(t.admin.accounts.disableConfirm(account.email))) return;
    setMessage(null);
    statusMutation.mutate({ id: account.id, enabled: nextEnabled });
  };

  const createAccount = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setMessage(null);
    createMutation.mutate(newAccount);
  };

  return (
    <main className="mx-auto max-w-[1500px] p-4 sm:p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t.admin.accounts.title}</h1>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.admin.accounts.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/book-builder" className="kitup-quiet-button">
            {t.admin.bookBuilder.title}
          </Link>
          <Link to="/admin/usage" className="kitup-quiet-button">
            {t.admin.usage.title}
          </Link>
          <Link to="/admin/promotions" className="kitup-quiet-button">
            {t.admin.promotions.title}
          </Link>
          <Link to="/admin/generations" className="kitup-quiet-button">
            {t.admin.accounts.toGenerations}
          </Link>
          <Link to="/app" className="kitup-quiet-button">
            {t.admin.toApp}
          </Link>
        </div>
      </div>

      <section className="kitup-card mb-5 grid gap-6 p-4 lg:grid-cols-2">
        <div className="grid content-start gap-3">
          <div>
            <h2 className="font-semibold">{t.admin.accounts.registrationTitle}</h2>
            <p className="text-sm text-[var(--kitup-color-muted)]">
              {t.admin.accounts.registrationHint}
            </p>
          </div>
          {registration.isPending ? <p>{t.common.loading}</p> : null}
          {registration.isError ? <p role="alert">{t.common.unexpectedError}</p> : null}
          {registration.data ? (
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                role="switch"
                checked={registration.data.registrationEnabled}
                disabled={registrationMutation.isPending}
                onChange={(event) => {
                  setMessage(null);
                  registrationMutation.mutate(event.target.checked);
                }}
              />
              <span className="font-medium">
                {registration.data.registrationEnabled
                  ? t.admin.accounts.registrationEnabled
                  : t.admin.accounts.registrationDisabled}
              </span>
            </label>
          ) : null}

          <div className="mt-2 grid gap-3 border-t border-[var(--kitup-color-muted)]/20 pt-4">
            <div>
              <h2 className="font-semibold">{t.admin.accounts.economyModeTitle}</h2>
              <p className="text-sm text-[var(--kitup-color-muted)]">
                {t.admin.accounts.economyModeHint}
              </p>
            </div>
            {economyMode.isPending ? <p>{t.common.loading}</p> : null}
            {economyMode.isError ? <p role="alert">{t.common.unexpectedError}</p> : null}
            {economyMode.data ? (
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  role="switch"
                  checked={economyMode.data.economyModeEnabled}
                  disabled={economyModeMutation.isPending}
                  onChange={(event) => {
                    setMessage(null);
                    economyModeMutation.mutate(event.target.checked);
                  }}
                />
                <span className="font-medium">
                  {economyMode.data.economyModeEnabled
                    ? t.admin.accounts.economyModeEnabled
                    : t.admin.accounts.economyModeDisabled}
                </span>
              </label>
            ) : null}
          </div>
        </div>

        <form className="grid gap-3" onSubmit={createAccount}>
          <div>
            <h2 className="font-semibold">{t.admin.accounts.createTitle}</h2>
            <p className="text-sm text-[var(--kitup-color-muted)]">{t.admin.accounts.createHint}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-sm">
              <span>{t.common.name}</span>
              <input
                required
                autoComplete="off"
                value={newAccount.name}
                onChange={(event) =>
                  setNewAccount((current) => ({ ...current, name: event.target.value }))
                }
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span>{t.common.email}</span>
              <input
                required
                type="email"
                autoComplete="off"
                value={newAccount.email}
                onChange={(event) =>
                  setNewAccount((current) => ({ ...current, email: event.target.value }))
                }
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span>{t.common.password}</span>
              <input
                required
                type="password"
                minLength={10}
                autoComplete="new-password"
                value={newAccount.password}
                onChange={(event) =>
                  setNewAccount((current) => ({ ...current, password: event.target.value }))
                }
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span>{t.admin.accounts.role}</span>
              <select
                value={newAccount.role}
                onChange={(event) =>
                  setNewAccount((current) => ({
                    ...current,
                    role: event.target.value as AccountRole,
                  }))
                }
              >
                <option value="user">{t.admin.accounts.roles.user}</option>
                <option value="admin">{t.admin.accounts.roles.admin}</option>
              </select>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={newAccount.emailVerified}
              onChange={(event) =>
                setNewAccount((current) => ({
                  ...current,
                  emailVerified: event.target.checked,
                }))
              }
            />
            <span>{t.admin.accounts.markEmailVerified}</span>
          </label>
          <button
            type="submit"
            className="kitup-primary-button justify-self-start"
            disabled={createMutation.isPending}
          >
            {t.admin.accounts.createSubmit}
          </button>
        </form>
      </section>

      <section aria-label={t.admin.accounts.summary} className="mb-5 grid gap-3 sm:grid-cols-4">
        <Summary label={t.admin.accounts.totalAccounts} value={all.length} />
        <Summary label={t.admin.accounts.enabled} value={enabledCount} tone="green" />
        <Summary label={t.admin.accounts.disabled} value={all.length - enabledCount} tone="red" />
        <Summary label={t.admin.accounts.totalBooks} value={booksCount} />
      </section>

      <section className="kitup-card mb-5 grid gap-3 p-4 md:grid-cols-[minmax(220px,1fr)_repeat(3,minmax(150px,auto))]">
        <label className="grid gap-1 text-sm">
          <span>{t.admin.accounts.search}</span>
          <input
            type="search"
            value={search}
            placeholder={t.admin.accounts.searchPlaceholder}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <Select
          label={t.admin.accounts.filter}
          value={filter}
          options={['all', 'enabled', 'disabled']}
          optionLabel={(value) => t.admin.accounts.filters[value as AccountFilter]}
          onChange={(value) => setFilter(value as AccountFilter)}
        />
        <Select
          label={t.admin.accounts.sort}
          value={sort}
          options={['createdAt', 'name', 'email', 'books', 'status']}
          optionLabel={(value) => t.admin.accounts.sorts[value as AdminAccountSort]}
          onChange={(value) => setSort(value as AdminAccountSort)}
        />
        <Select
          label={t.admin.accounts.direction}
          value={direction}
          options={['desc', 'asc']}
          optionLabel={(value) => t.admin.accounts.directions[value as AdminAccountDirection]}
          onChange={(value) => setDirection(value as AdminAccountDirection)}
        />
      </section>

      {message ? (
        <p role="status" className="mb-4 text-sm">
          {message}
        </p>
      ) : null}
      {accounts.isPending ? <p>{t.common.loading}</p> : null}
      {accounts.isError ? <p role="alert">{t.common.unexpectedError}</p> : null}
      {!accounts.isPending && !accounts.isError && visible.length === 0 ? (
        <p className="kitup-card p-5">{t.admin.accounts.empty}</p>
      ) : null}

      <div className="grid gap-3">
        {visible.map((account) => (
          <article key={account.id} className="kitup-card overflow-hidden">
            <div className="grid items-center gap-4 p-4 md:grid-cols-[minmax(240px,1.2fr)_170px_minmax(280px,1fr)_auto]">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate font-semibold">{account.name}</h2>
                  {account.role === 'admin' ? (
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-800">
                      {t.admin.accounts.adminRole}
                    </span>
                  ) : null}
                  <StatusBadge enabled={account.enabled} />
                </div>
                <p className="truncate text-sm">{account.email}</p>
                <p className="text-xs text-[var(--kitup-color-muted)]">
                  {t.admin.accounts.registered}: {formatDate(account.createdAt)} ·{' '}
                  {account.emailVerified
                    ? t.admin.accounts.emailVerified
                    : t.admin.accounts.emailNotVerified}
                </p>
              </div>

              <div>
                <p className="text-xs text-[var(--kitup-color-muted)]">{t.admin.accounts.books}</p>
                <p className="text-2xl font-semibold">{account.bookCounts.total}</p>
              </div>

              <BookCounts counts={account.bookCounts} />

              <div className="grid gap-2">
                <Link
                  to={`/admin/promotions?email=${encodeURIComponent(account.email)}`}
                  className="kitup-quiet-button"
                >
                  {t.admin.accounts.givePromo}
                </Link>
                <button
                  type="button"
                  className={account.enabled ? 'kitup-secondary-button' : 'kitup-primary-button'}
                  disabled={statusMutation.isPending || (account.id === user.id && account.enabled)}
                  title={
                    account.id === user.id && account.enabled
                      ? t.admin.accounts.cannotDisableSelf
                      : undefined
                  }
                  onClick={() => changeStatus(account)}
                >
                  {account.enabled ? t.admin.accounts.disable : t.admin.accounts.enable}
                </button>
              </div>
            </div>

            {account.books.length > 0 ? (
              <details className="border-t border-[var(--kitup-color-muted)]/20 px-4 py-3">
                <summary className="cursor-pointer text-sm font-medium">
                  {t.admin.accounts.showBooks(account.books.length)}
                </summary>
                <ul className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {account.books.map((book) => (
                    <li key={book.id} className="rounded-lg bg-[var(--kitup-color-paper)] p-3">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium">{book.title}</span>
                        <BookStatusBadge status={book.status} />
                      </div>
                      <p className="mt-1 text-xs text-[var(--kitup-color-muted)]">
                        {t.admin.accounts.updated}: {formatDate(book.updatedAt)}
                      </p>
                      <Link
                        to={`/admin/accounts/${account.id}/books/${book.id}`}
                        className="kitup-quiet-button mt-2"
                      >
                        {t.admin.accounts.openBook}
                      </Link>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </article>
        ))}
      </div>
    </main>
  );
}

function Summary({
  label,
  value,
  tone = 'plain',
}: {
  label: string;
  value: number;
  tone?: 'plain' | 'green' | 'red';
}): JSX.Element {
  const color = tone === 'green' ? 'text-emerald-700' : tone === 'red' ? 'text-rose-700' : '';
  return (
    <div className="kitup-card p-4">
      <p className="text-xs text-[var(--kitup-color-muted)]">{label}</p>
      <p className={`text-2xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function StatusBadge({ enabled }: { enabled: boolean }): JSX.Element {
  const { t } = useI18n();
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs ${enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}
    >
      {enabled ? t.admin.accounts.enabled : t.admin.accounts.disabled}
    </span>
  );
}

function BookCounts({ counts }: { counts: AdminAccountDto['bookCounts'] }): JSX.Element {
  const { t } = useI18n();
  const statuses: readonly AdminBookStatus[] = ['draft', 'generating', 'ready', 'archived'];
  return (
    <div className="flex flex-wrap gap-2">
      {statuses.map((status) => (
        <span
          key={status}
          className="rounded-full bg-[var(--kitup-color-paper)] px-2.5 py-1 text-xs"
        >
          {t.admin.accounts.bookStatuses[status]}: {counts[status]}
        </span>
      ))}
    </div>
  );
}

function BookStatusBadge({ status }: { status: AdminBookStatus }): JSX.Element {
  const { t } = useI18n();
  return (
    <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs">
      {t.admin.accounts.bookStatuses[status]}
    </span>
  );
}

function Select({
  label,
  value,
  options,
  optionLabel,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  optionLabel(value: string): string;
  onChange(value: string): void;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
    </label>
  );
}
